import { useCallback, useRef, useState } from 'react';
import { Alert, Button, Stack } from '@mui/material'
import {
	ContentCopy as CsvCopyIcon
 } from '@mui/icons-material';
 import { CloseIcon } from 'shared/icons'

import { DialogBase } from 'components/dialog'
import { useSortableList, SortableList } from "components/controls/sortable/list";

import {
	PredictionCondition,
	PredictionData
} from 'interface/carenet/prediction'

import { PredictionHistory } from '../components/predictionHistory'
import { clipCsvData } from 'components/download/csv';
import { round } from 'shared/util';
import { usePrediction } from 'features/prediction/hooks';

type PredictionHistoryDialogProps = {
	predictionHistory: PredictionData[]
	setPredictionHistory: ReturnType<typeof usePrediction>["setPredictionHistory"]
	onSelectedCondition: (condition: PredictionCondition) => void
  onClose: () => void
}
export const PredictionHistoryDialog = ({
	predictionHistory,
	setPredictionHistory,
	onSelectedCondition,
  onClose
}: PredictionHistoryDialogProps) => {
  const sortableId = useRef<number>(0)
	const sortable = useSortableList({
		list: predictionHistory,
		toSortable: (data) => {
			sortableId.current = sortableId.current + 1
			return { ...data, id: sortableId.current }
		}
	})
	const { sortableList } = sortable
	const [message, setMessage] = useState<string | null>(null)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)

	const onClearHistory = useCallback(
		() => {
			setPredictionHistory([])
			onClose()
		},
		[setPredictionHistory, onClose]
	)
	const onCsvCopy = useCallback(
		async () => {
			try {
				setErrorMessage(null)
				setMessage(null)

				if (!await clipCsvData({
					header: [
						{ title: 'サービス', key: 'service' },
						{ title: '対象', key: 'target' },
						{ title: '人数', key: 'targetPerson' },
						{ title: '条件', key: 'condition' },
						{ title: '予測視聴率', key: 'viewRate' },
						{ title: '予測視聴人数', key: 'viewPerson' },
					],
					body: sortableList.map(history => ({
						service: history.predictionCondition.source == 'lv' ? 'Web講演会' :
										 history.predictionCondition.source == 'mrp' ? 'MRPlus' : '?',
						target: history.uploadResult.sharedList.condition ?? '全員',
						targetPerson: history.uploadResult.postData.recordCount,
						condition: history.predictionCondition.text ?? '',
						viewRate: round(history.predictionResult.predictedViewRate, 2),
						viewPerson: round((
							history.predictionResult.predictedViewRate * 
							history.uploadResult.postData.recordCount
						) / 100)
					}))
				})) {
					setErrorMessage('データのコピーに失敗しました')
					return
				}
				setMessage(`${sortableList.length}件のデータをコピーしました`)
			} catch (e) {
				console.error('データのコピーに失敗', e)
				setErrorMessage('データのコピーに失敗しました')
			}
		},
		[sortableList, setErrorMessage, setMessage]
	)
	return (
    <DialogBase
      open
			title="視聴予測履歴"
      fullWidth
      maxWidth="sm"
      body={
				<Stack direction='column' height=''>
					<SortableList
						listProps={{
							sx: {
								width: '100%'
							}
						}}
						renderItem={(history) => (
							<PredictionHistory
								history={history}
								onSelectedCondition={onSelectedCondition}
							/>
						)}
						{...sortable}
					/>
					{errorMessage && <Alert severity="error">{errorMessage}</Alert>}
				</Stack>
			}
			notice={message}
			action={{
				left: (
					<>
						<Button
							variant="contained"
							onClick={onCsvCopy}
							startIcon={<CsvCopyIcon/>}
						>
							コピー
						</Button>
					</>
				),
				right: (
					<>
						<Button
							variant="contained"
							color="error"
							onClick={onClearHistory}
						>
							結果をクリア
						</Button>
						<Button
							variant="text"
							onClick={onClose}
							startIcon={<CloseIcon/>}
						>
							閉じる
						</Button>
					</>
				)
			}}
      onClose={onClose}
      contentProps={{
        sx: {
					paddingX: 0,
          height: '60vh'
        }
      }}
    />
  )
}
